import React, { useState } from "react";
import { Box, Container, Typography, Button, Card, CardContent, AppBar, Toolbar, useTheme, alpha, IconButton, Menu, MenuItem } from "@mui/material";
import { AttachMoney as MoneyIcon, Share as ShareIcon, People as PeopleIcon, Menu as MenuIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useBranding } from "../../context/BrandingContext";
import { useDocumentMeta } from "../../hooks/useDocumentMeta";
import Logo from "../../components/Logo";

const Home: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { branding } = useBranding();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => setAnchorEl(null);
  const handleNavigate = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  // Atualiza o título e favicon da página
  useDocumentMeta();

  // Mesma regra da AppBar (App.tsx): fundo sólido opcional ou gradiente SIDEBAR_*
  const bannerBackground = branding.colors.headerBackground?.trim() ? branding.colors.headerBackground : `linear-gradient(to right, ${branding.colors.sidebarStart}, ${branding.colors.sidebarEnd})`;
  const onHeader = branding.colors.headerAction;
  // Botão de destaque no hero: fundo = cor de ação; texto = fundo da faixa (contraste)
  const heroCtaTextColor = branding.colors.headerBackground?.trim() ? branding.colors.headerBackground : branding.colors.sidebarStart;

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          background: bannerBackground,
          boxShadow: "none",
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            flexWrap: "wrap",
            bgcolor: "transparent",
            color: onHeader,
          }}
        >
          <Box sx={{ flexGrow: 1 }}>
            <Logo color={onHeader} />
          </Box>
          <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center" }}>
            {/* color="inherit" evita que o tema primary sobrescreva HEADER_ACTION_COLOR */}
            <Button color="inherit" onClick={() => navigate("/login")}>
              Entrar
            </Button>
            <Button
              color="inherit"
              variant="outlined"
              onClick={() => navigate("/signup")}
              sx={{
                ml: 1,
                borderColor: alpha(onHeader, 0.55),
                "&:hover": {
                  borderColor: onHeader,
                  bgcolor: alpha(onHeader, 0.08),
                },
              }}
            >
              Cadastrar
            </Button>
          </Box>
          <IconButton
            color="inherit"
            aria-label="menu"
            aria-controls={menuOpen ? "nav-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? "true" : undefined}
            onClick={handleMenuOpen}
            sx={{
              display: { xs: "block", sm: "none" },
            }}
          >
            <MenuIcon />
          </IconButton>
          <Menu id="nav-menu" anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
            <MenuItem onClick={() => handleNavigate("/login")}>Entrar</MenuItem>
            <MenuItem onClick={() => handleNavigate("/signup")}>Cadastrar</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          background: bannerBackground,
          color: onHeader,
          py: 10,
          textAlign: "center",
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h2" fontWeight="bold" gutterBottom>
            Ganhe dinheiro indicando
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, opacity: 0.92 }}>
            Cadastre-se, compartilhe seu link e ganhe comissões por cada cliente que fechar
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate("/signup")}
            sx={{
              bgcolor: onHeader,
              color: heroCtaTextColor,
              "&:hover": { bgcolor: alpha(onHeader, 0.92) },
              px: 4,
              py: 1.5,
              fontSize: "1.1rem",
            }}
          >
            Começar agora
          </Button>
        </Container>
      </Box>

      <Container
        sx={{
          py: { xs: 6, md: 10 },
          bgcolor: theme.palette.background.default,
        }}
      >
        <Typography
          variant="h4"
          align="center"
          fontWeight="bold"
          gutterBottom
          sx={{
            letterSpacing: "-0.02em",
            color: theme.palette.text.primary,
          }}
        >
          Como funciona
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 6 }}>
          Em 3 passos simples você começa a ganhar
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 3,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          {[
            {
              step: "1. Cadastre-se",
              text: "Crie sua conta gratuitamente e receba um link exclusivo de indicação",
              icon: PeopleIcon,
            },
            {
              step: "2. Compartilhe",
              text: "Envie seu link para amigos, familiares e conhecidos interessados",
              icon: ShareIcon,
            },
            {
              step: "3. Ganhe",
              text: "Receba comissões por cada cliente que fechar negócio através do seu link",
              icon: MoneyIcon,
            },
          ].map((item) => (
            <Card
              key={item.step}
              elevation={0}
              sx={{
                textAlign: "center",
                p: 3,
                flex: 1,
                minWidth: 280,
                maxWidth: 350,
                borderRadius: 3,
                border: "1px solid",
                borderColor: alpha(theme.palette.primary.main, 0.12),
                bgcolor: theme.palette.background.paper,
                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.06)}`,
                transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.12)}`,
                  borderColor: alpha(theme.palette.primary.main, 0.25),
                },
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px",
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                  }}
                >
                  <item.icon
                    sx={{
                      fontSize: 36,
                      color: theme.palette.primary.main,
                    }}
                  />
                </Box>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  {item.step}
                </Typography>
                <Typography color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {item.text}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Container>

      <Box
        sx={{
          bgcolor: theme.palette.background.default,
          py: 8,
          textAlign: "center",
        }}
      >
        <Container>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Pronto para começar?
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 4 }}>
            Junte-se a centenas de afiliados que já estão ganhando com indicações
          </Typography>
          <Button variant="contained" size="large" onClick={() => navigate("/signup")} sx={{ px: 4, py: 1.5 }}>
            Criar minha conta grátis
          </Button>
        </Container>
      </Box>

      <Box
        component="footer"
        sx={{
          py: 3,
          textAlign: "center",
          background: bannerBackground,
          color: onHeader,
          mt: "auto",
        }}
      >
        <Typography variant="body2">
          {branding.platformName} © {new Date().getFullYear()} - Todos os direitos reservados
        </Typography>
      </Box>
    </Box>
  );
};

export default Home;
